/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useMemo,
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Button } from "@/src/components/ui/button";
import {
  type ColumnOrderState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Menu } from "lucide-react";
import { type LangfuseColumnDef } from "@/src/components/table/types";
import { usePostHogClientCapture } from "@/src/features/posthog-analytics";
import DocPopup from "@/src/components/layouts/doc-popup";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { cn } from "@/src/utils/tailwind";
import { isString } from "@/src/utils/types";
import { PopoverController } from "@/src/components/ui/popover";
import { ColumnVisibilityHeader } from "@/src/components/table/ColumnVisibilityHeader";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/src/components/ui/collapsible";
import { Checkbox } from "@/src/components/design-system/Checkbox/Checkbox";
import { Separator } from "@/src/components/ui/separator";

/**
 * A whole column GROUP was shown or hidden at once (its "Select All" /
 * "Deselect All"). Surfaces that care which family a group is — the experiments
 * tables and their score levels — hook in here rather than having this generic
 * picker know about them.
 */
export type ColumnGroupTogglePayload = {
  /** The group column's accessorKey. */
  groupId: string;
  /** Columns visible in the group after the toggle: all of them, or none. */
  enabledCount: number;
  /** Columns in the group. */
  totalCount: number;
};

interface ColumnVisibilityProps<TData, TValue> {
  columns: LangfuseColumnDef<TData, TValue>[];
  columnVisibility: VisibilityState;
  setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>;
  columnOrder?: ColumnOrderState;
  setColumnOrder?: Dispatch<SetStateAction<ColumnOrderState>>;
  tableName?: string;
  isV4?: boolean;
  onColumnGroupToggle?: (payload: ColumnGroupTogglePayload) => void;
  triggerSize?: ComponentProps<typeof Button>["size"];
  additionalColumnSettings?: {
    content: React.ReactNode;
    isDefault: boolean;
    onRestoreDefaults: () => void;
  };
}

/**
 * A column's name for the picker: its own label before its rendered header, so
 * a column whose header carries more than its name still reads here.
 */
const getColumnLabel = <TData, TValue>(
  column: LangfuseColumnDef<TData, TValue>,
) =>
  column.headerLabel ??
  (typeof column.header === "string" && column.header
    ? column.header
    : column.accessorKey);

const calculateColumnCounts = <TData, TValue>(
  columns: LangfuseColumnDef<TData, TValue>[],
  columnVisibility: VisibilityState,
) => {
  return columns.reduce(
    (acc, column) => {
      if (column.columns) {
        const groupCounts = calculateColumnCounts(
          column.columns,
          columnVisibility,
        );
        acc.count += groupCounts.count;
        acc.total += groupCounts.total;
      } else {
        acc.total++;
        if (
          (column.accessorKey in columnVisibility &&
            columnVisibility[column.accessorKey]) ||
          !column.enableHiding
        ) {
          acc.count++;
        }
      }
      return acc;
    },
    { count: 0, total: 0 },
  );
};

function hasVisibilityChanges<TData, TValue>(
  columns: LangfuseColumnDef<TData, TValue>[],
  columnVisibility: VisibilityState,
): boolean {
  return columns.some((column) =>
    column.columns?.length
      ? hasVisibilityChanges(column.columns, columnVisibility)
      : column.enableHiding &&
        !column.isFixedPosition &&
        (columnVisibility[column.accessorKey] ?? !column.defaultHidden) !==
          !column.defaultHidden,
  );
}

function ColumnVisibilityListItem<TData, TValue>({
  column,
  toggleColumn,
  columnVisibility,
  isOrderable = false,
}: {
  column: LangfuseColumnDef<TData, TValue>;
  toggleColumn: (columnId: string) => void;
  columnVisibility: VisibilityState;
  isOrderable?: boolean;
}) {
  const isFixedPosition = column.isFixedPosition ?? false;
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: column.accessorKey,
      disabled: !isOrderable || isFixedPosition,
    });

  const isChecked = columnVisibility[column.accessorKey] && column.enableHiding;
  const isLocked = !column.enableHiding || isFixedPosition;
  const checkboxId = `col-${column.accessorKey}`;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5",
        isDragging ? "opacity-80" : "opacity-100",
        "hover:bg-muted/50 group transition-colors",
      )}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition: isDragging ? "none" : "transform 0.15s ease-in-out",
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={isChecked || isLocked}
          onCheckedChange={() => {
            if (!isLocked) toggleColumn(column.accessorKey);
          }}
          disabled={isLocked}
        />
        <label
          htmlFor={checkboxId}
          className={cn(
            "min-w-0 text-sm break-words capitalize",
            isLocked ? "opacity-50" : "cursor-pointer",
          )}
          title={
            !column.enableHiding
              ? "This column may not be hidden"
              : isFixedPosition
                ? "This column is fixed in position and cannot be hidden"
                : undefined
          }
        >
          {getColumnLabel(column)}
        </label>
        {column.headerTooltip && (
          <DocPopup
            description={column.headerTooltip.description}
            href={column.headerTooltip.href}
          />
        )}
      </div>

      {isOrderable && !isFixedPosition && (
        <Button
          {...attributes}
          {...listeners}
          variant="ghost"
          size="xs"
          title="Drag and drop to reorder columns"
          className="invisible shrink-0 group-focus-within:visible group-hover:visible"
        >
          <Menu className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function GroupVisibilityHeader<TData, TValue>({
  column,
  groupTotalCount,
  groupVisibleCount,
  isOpen,
  onToggle,
  children,
  toggleAll,
}: {
  column: LangfuseColumnDef<TData, TValue>;
  groupTotalCount: number;
  groupVisibleCount: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  toggleAll: () => void;
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: column.accessorKey,
    });

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <div
        ref={setNodeRef}
        className={cn(
          "bg-muted/30 group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5",
          isDragging ? "opacity-80" : "opacity-100",
          "hover:bg-muted",
        )}
        style={{
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
            : undefined,
          transition: isDragging ? "none" : "transform 0.15s ease-in-out",
          zIndex: isDragging ? 1 : undefined,
        }}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left"
          >
            {isOpen ? (
              <ChevronDown className="size-4 shrink-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100" />
            ) : (
              <ChevronRight className="size-4 shrink-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100" />
            )}
            <span className="min-w-0 text-sm font-bold">
              {getColumnLabel(column)}
            </span>
            <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">
              ({groupVisibleCount}/{groupTotalCount})
            </span>
          </button>
        </CollapsibleTrigger>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-5 px-2 text-xs group-focus-within:visible group-hover:visible",
              !isOpen && "invisible",
            )}
            onClick={toggleAll}
          >
            {groupVisibleCount === groupTotalCount
              ? "Deselect All"
              : "Select All"}
          </Button>
          {attributes && listeners && (
            <Button
              {...attributes}
              {...listeners}
              variant="ghost"
              size="xs"
              title="Drag and drop to reorder columns"
              className="invisible group-focus-within:visible group-hover:visible"
            >
              <Menu className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
      <CollapsibleContent className="pt-0.5 pl-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function setAllColumns<TData, TValue>(
  columns: LangfuseColumnDef<TData, TValue>[],
  visible: boolean,
  groupName?: string,
) {
  return (oldVisibility: VisibilityState) => {
    const newColumnVisibility: VisibilityState = { ...oldVisibility };
    columns.forEach((col) => {
      if (groupName && col.header === groupName && col.columns) {
        col.columns.forEach((subCol) => {
          if (subCol.enableHiding)
            newColumnVisibility[subCol.accessorKey] = visible;
        });
      } else if (!groupName && col.enableHiding) {
        newColumnVisibility[col.accessorKey] = visible;
        if (col.columns) {
          col.columns.forEach((subCol) => {
            newColumnVisibility[subCol.accessorKey] = visible;
          });
        }
      }
    });
    return newColumnVisibility;
  };
}

export function DataTableColumnVisibilityFilter<TData, TValue>({
  columns,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
  triggerSize,
  additionalColumnSettings,
  tableName = "unknown",
  isV4 = false,
  onColumnGroupToggle,
}: ColumnVisibilityProps<TData, TValue>) {
  const capture = usePostHogClientCapture();
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    {},
  );

  const { defaultColumnOrder, defaultColumnVisibility } = useMemo(() => {
    return {
      defaultColumnOrder: columns.map((col) => col.accessorKey),
      defaultColumnVisibility: columns.reduce((acc, col) => {
        acc[col.accessorKey] = !col.defaultHidden;
        col.columns?.forEach((subCol) => {
          acc[subCol.accessorKey] = !subCol.defaultHidden;
        });
        return acc;
      }, {} as VisibilityState),
    };
  }, [columns]);

  const toggleColumn = useCallback(
    (columnId: string) => {
      // calculate target state outside of setState to make it idempotent
      const currentValue = columnVisibility[columnId];
      const targetValue = !currentValue;
      // The event belongs to the click, not to the state updater: an updater
      // must be pure, and React invokes it twice under StrictMode — which
      // double-counted every toggle. Same payload, emitted once.
      const nextVisibility = { ...columnVisibility, [columnId]: targetValue };
      capture("table:column_visibility_changed", {
        selectedColumns: Object.keys(nextVisibility).filter(
          (key) => nextVisibility[key],
        ),
        tableName,
        isV4,
      });
      setColumnVisibility((old: any) => ({
        ...old,
        [columnId]: targetValue,
      }));
    },
    // eslint disable is because we don't want the posthog capture as deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setColumnVisibility, columnVisibility, tableName, isV4],
  );

  const toggleAllColumns = useCallback(
    (count: number, total: number, groupName?: string) => {
      if (count === total) {
        setColumnVisibility(setAllColumns(columns, false, groupName));
      } else {
        setColumnVisibility(setAllColumns(columns, true, groupName));
      }
    },
    [setColumnVisibility, columns],
  );

  const toggleGroup = (columnId: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const { count, total } = calculateColumnCounts(columns, columnVisibility);
  // Shape-checked, not just nullish-checked: ~30 tables share this picker and
  // a persisted order can come back as anything.
  const columnIdsOrder = Array.isArray(columnOrder)
    ? columnOrder
    : columns.map((col) => col.accessorKey);
  const isColumnOrderingEnabled = !!setColumnOrder;
  const hasOrderChanges =
    isColumnOrderingEnabled &&
    (columnIdsOrder.length !== defaultColumnOrder.length ||
      columnIdsOrder.some((id, index) => id !== defaultColumnOrder[index]));
  const hasChanges =
    hasVisibilityChanges(columns, columnVisibility) ||
    hasOrderChanges ||
    (additionalColumnSettings && !additionalColumnSettings.isDefault);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const activeColumn = columns.find((col) => col.accessorKey === active.id);
      const overColumn = columns.find((col) => col.accessorKey === over.id);

      // Prevent reordering if either active or over column is fixed position
      if (activeColumn?.isFixedPosition || overColumn?.isFixedPosition) {
        return;
      }

      if (isString(active.id) && isString(over.id)) {
        setColumnOrder!((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id as string);
          const newIndex = columnOrder.indexOf(over.id as string);
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
      }
    }
  }

  return (
    <PopoverController
      align="end"
      contentClassName="max-h-[min(70vh,var(--radix-popover-content-available-height))] w-90 max-w-[calc(100vw-1rem)] overflow-y-auto p-0"
      disabled={false}
      modal={false}
      renderContent={() => (
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={isColumnOrderingEnabled ? handleDragEnd : undefined}
          sensors={sensors}
        >
          <div className="w-full">
            <ColumnVisibilityHeader
              onRestoreDefaults={
                hasChanges
                  ? () => {
                      setColumnOrder?.(defaultColumnOrder);
                      setColumnVisibility(defaultColumnVisibility);
                      additionalColumnSettings?.onRestoreDefaults();
                    }
                  : undefined
              }
            />
            <div className="p-1">
              <Button
                variant="ghost"
                className="w-full justify-between px-2"
                onClick={() => toggleAllColumns(count, total)}
              >
                <span>
                  {count === total
                    ? "Deselect All Columns"
                    : "Select All Columns"}
                </span>
                <span className="text-muted-foreground text-xs">{`${count}/${total}`}</span>
              </Button>
            </div>
            <Separator />
            <div className="p-1">
              <SortableContext
                items={columnIdsOrder}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-0.5">
                  {columnIdsOrder.map((columnId) => {
                    const column = columns.find(
                      (col) => col.accessorKey === columnId,
                    );
                    if (!column) return null;

                    if (!!column.columns && column.columns.length > 0) {
                      // Column groups
                      const groupTotalCount = column.columns.length;
                      const groupVisibleCount = column.columns.filter(
                        (col) => columnVisibility[col.accessorKey],
                      ).length;

                      return (
                        <GroupVisibilityHeader
                          key={column.accessorKey}
                          column={column}
                          groupTotalCount={groupTotalCount}
                          groupVisibleCount={groupVisibleCount}
                          isOpen={!!openGroups[column.accessorKey]}
                          onToggle={() => toggleGroup(column.accessorKey)}
                          toggleAll={() => {
                            if (
                              column.header &&
                              typeof column.header === "string"
                            ) {
                              const enabling =
                                groupVisibleCount !== groupTotalCount;
                              toggleAllColumns(
                                groupVisibleCount,
                                groupTotalCount,
                                column.header,
                              );
                              onColumnGroupToggle?.({
                                groupId: column.accessorKey,
                                enabledCount: enabling ? groupTotalCount : 0,
                                totalCount: groupTotalCount,
                              });
                            }
                          }}
                        >
                          <div className="space-y-0.5">
                            {column.columns.map((col) => (
                              <ColumnVisibilityListItem
                                key={col.accessorKey}
                                column={col}
                                columnVisibility={columnVisibility}
                                toggleColumn={toggleColumn}
                                isOrderable={false}
                              />
                            ))}
                          </div>
                        </GroupVisibilityHeader>
                      );
                    }
                    // Single columns
                    return (
                      <ColumnVisibilityListItem
                        key={column.accessorKey}
                        column={column}
                        columnVisibility={columnVisibility}
                        toggleColumn={toggleColumn}
                        isOrderable={isColumnOrderingEnabled}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </div>
            {additionalColumnSettings && (
              <>
                <Separator />
                {additionalColumnSettings.content}
              </>
            )}
          </div>
        </DndContext>
      )}
    >
      {({ Trigger }) => (
        <Trigger asChild>
          <Button
            variant="outline"
            size={triggerSize}
            title="Show/hide columns"
          >
            <span>Columns</span>
            <div className="bg-input ml-1 rounded-sm px-1 text-xs">{`${count}/${total}`}</div>
          </Button>
        </Trigger>
      )}
    </PopoverController>
  );
}
